import { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Row } from 'reactstrap'
import images from '../../api/images/hooks'
import { Image } from '../../api/images/types'
import { MODAL_TYPES } from '../../common/constants'
import { showModal } from '../../store/modal'
import FormMainContainer from '../Common/FormMainContainer'
import FormSideContainer from '../Common/FormSideContainer'
import FormTabNavigation from '../Common/FormTabNavigation'
import Dropzone from '../Dropzone/Dropzone'
import { setDeleteCallback } from '../Modals/DeleteModal/utils'
import ToastImageEditor from '../ToastImageEditor/ToastImageEditor'
import ImagesActions from './ImagesActions'
import ImagesGallery from './ImagesGallery'
import { ImagesFormTabProps } from './types'

const ImagesFormTab = (props: ImagesFormTabProps) => {
    const dispatch = useDispatch()

    const {
        module,
        collection,
        url,
        entityData,
        isFetching,
        isSubmitting,
        activeTab,
        activeChildTab,
        tabs = [],
        childTabs = [],
        imageData,
        handleTabChange,
        handleChildTabChange,
    } = props

    const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set())
    const [editorImage, setEditorImage] = useState<Image | null>(null)

    const { mutate: uploadImages } = images.UploadImages.useMutation()
    const { mutate: deleteImages } = images.DeleteImages.useMutation()
    const { mutate: syncImage } = images.SyncImage.useMutation()

    const queryPayload = useMemo(() => ({ collection, module, parent: entityData?.parent }), [collection, module, entityData?.parent])

    const imageList = useMemo(() => {
        return imageData.map((file) => ({
            ...file,
            checked: checkedIds.has(file.id),
        }))
    }, [imageData, checkedIds])

    const selectedImagesIds = useMemo(() => {
        return imageList.filter((image) => image.checked).map((image) => image.id.toString())
    }, [imageList])

    const handleImagesDelete = () => {
        const callbackId = 'delete-images'
        const payload = {
            modalType: MODAL_TYPES.DELETE_MODAL,
            modalProps: { callbackId },
        }

        setDeleteCallback(callbackId, () => deleteImages({ ids: selectedImagesIds, ...queryPayload }))
        dispatch(showModal(payload))
    }

    const handleImagesUpload = useCallback(
        (files: File[]) => {
            uploadImages({ files, entityData, url, collection, module })
        },
        [uploadImages, entityData, url, collection, module]
    )

    const handleEditorImage = useCallback((image: Image) => {
        setEditorImage(image)
    }, [])

    const handleSelectImage = useCallback((imageId: number) => {
        setCheckedIds((prev) => {
            const newSet = new Set(prev)
            newSet.has(imageId) ? newSet.delete(imageId) : newSet.add(imageId)

            return newSet
        })
    }, [])

    const handleSelectAll = useCallback(() => {
        setCheckedIds(() => {
            const allChecked = imageList.every((image) => image.checked)

            return allChecked ? new Set() : new Set(imageList.map((image) => image.id))
        })
    }, [imageList])

    return (
        <Row>
            <FormSideContainer>
                {activeTab === 'materials' && (
                    <Dropzone
                        callback={handleImagesUpload}
                        tab={activeChildTab}
                        module={module}
                        parent={entityData?.parent}
                        existingFilesLength={imageData.length}
                    />
                )}
            </FormSideContainer>

            <FormMainContainer tabs={tabs} activeTab={activeTab} handleTabChange={handleTabChange} formik={null}>
                <div className="d-flex justify-content-between align-items-center">
                    {childTabs.length > 0 && (
                        // TODO: convert FormTabNavigation to TS
                        <FormTabNavigation
                            handleTabChange={handleChildTabChange}
                            tabs={childTabs}
                            activeTab={activeChildTab}
                            customClass="real-estate-form-subtabs mb-4"
                        >
                            {null}
                        </FormTabNavigation>
                    )}

                    {imageData.length > 0 && (
                        <ImagesActions
                            imageData={imageData}
                            editorImage={editorImage}
                            selectedImagesIds={selectedImagesIds}
                            setEditorImage={setEditorImage}
                            handleSelectAll={handleSelectAll}
                            handleImagesDelete={handleImagesDelete}
                        />
                    )}
                </div>

                <Row className="mb-5">{editorImage && <ToastImageEditor key={editorImage.id} image={editorImage} isSubmitting={isSubmitting} />}</Row>

                {imageData.length > 0 && (
                    <ImagesGallery
                        imageList={imageList}
                        imageData={imageData}
                        module={module}
                        queryPayload={queryPayload}
                        isFetching={isFetching}
                        handleEditorImage={handleEditorImage}
                        handleSelectImage={handleSelectImage}
                        syncImage={syncImage}
                    />
                )}
            </FormMainContainer>
        </Row>
    )
}

export default ImagesFormTab
